import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withMountListener } from '@beef/react';
import { useCustomEvent } from '@beef/shop-common';
import { compose } from '@reduxjs/toolkit';

import { OutOfStockPopup } from 'components';
import { COOKIES_NAMES } from 'constants/cookies';
import { advCakeEvents } from 'pushEvent/advCakeEvents';
import {
  getBasketData as getBasketDataSelector,
  isBasketDataFetchedSelector,
} from 'store/common/basket/selectors';
import { isScreenBelow767 } from 'store/common/settings/breakpoint/selectors';
import {
  onOpenProductDetails,
  setProductIsFavorite,
  setProductToggleCompare,
} from 'store/ProductDetails/common/actions';
import {
  DETAILS_VIEW_OUT_OF_STOCK,
  DETAILS_VIEW_PREORDER,
  DETAILS_VIEW_TARIFF,
} from 'store/ProductDetails/constants';
import { setProductDeviceDetailsIsFavoriteData } from 'store/ProductDetails/detailsModal/actions';
import rootReducer from 'store/ProductDetails/reducers';
import { getDetailsViewSlug } from 'store/ProductDetails/selectors';
import { setSimilarProductsIsFavorite } from 'store/ProductDetails/similarProducts/actions';
import { getBodyData } from 'store/selectors';
import connectProvider from 'utils/redux-provider';
import { PartnerOrder, SubscribeForm } from 'widgets';

import PageLayout from '../_PageLayout';
import { DetailsModal } from './components';
import { convertLegacyModel } from './dataConverter';
import OutOfStockView from './views/OutOfStock';
import PreorderView from './views/Preorder';
import SimpleView from './views/Simple';
import TariffView from './views/Tariff';

const productPages = {
  [DETAILS_VIEW_OUT_OF_STOCK]: OutOfStockView,
  [DETAILS_VIEW_PREORDER]: PreorderView,
  [DETAILS_VIEW_TARIFF]: TariffView,
};

/**
 * Full Product Details page, connected to store and containing layout
 */
const ProductDetailsPageSwitch = ({ basketData, isBasketDataFetched, isMobile, product, type }) => {
  const { subscribeDOM } = useCustomEvent();

  useEffect(() => {
    if (product && isBasketDataFetched) {
      advCakeEvents.setEventForProduct({ basketData, product });
    }
  }, [product, isBasketDataFetched]);

  useEffect(() => {
    const unSubscribeFavorites = subscribeDOM(COOKIES_NAMES.FAVORITE_DEVICES, (favoritesList) => {
      setSimilarProductsIsFavorite(favoritesList);
      setProductDeviceDetailsIsFavoriteData(favoritesList);
      setProductIsFavorite(favoritesList.includes(product.productId));
    });

    const unSubscribeCmp = subscribeDOM(COOKIES_NAMES.COMPARISON_DEVICES, () => {
      setProductToggleCompare();
    });

    return () => {
      unSubscribeFavorites();
      unSubscribeCmp();
    };
  }, []);

  const PageDetail = productPages[type];
  const isPageDetail = !!PageDetail;

  return (
    <>
      <PageLayout onlyCrumbs={isMobile}>
        {isPageDetail ?
          <PageDetail />
        : <SimpleView />}
      </PageLayout>
      <DetailsModal />
      <OutOfStockPopup />
    </>
  );
};

/**
 * Standalone connected Product Details Page
 */

const mapStateToProps = (state) => ({
  basketData: getBasketDataSelector(state),
  isBasketDataFetched: isBasketDataFetchedSelector(state),
  isMobile: isScreenBelow767(state),
  product: getBodyData(state),
  type: getDetailsViewSlug(state),
});

const mapDispatchToProps = {
  onMount: onOpenProductDetails,
  setProductDeviceDetailsIsFavoriteData,
  setProductIsFavorite,
  setProductToggleCompare,
  setSimilarProductsIsFavorite,
};

const ConnectedProductDetails = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withMountListener,
)(ProductDetailsPageSwitch);

export default connectProvider(rootReducer, convertLegacyModel, {
  SubscribeForm,
  partnerSettings: PartnerOrder,
})(ConnectedProductDetails);
